import config from '@/config'
const { cdn } = config
const { timestamp, isGoogleEventsReady, getClientID, dataLayerPush, getQueryString } = require('@/utilities')
const GCLID = getQueryString('GCLID') || ''

export default {
  namespaced: true,
  state: () => ({
    utmSource: '',
    gaClientID: 'N/A',
    GCLID,
    acceptedCSATimestamp: null,
    hasAcceptedTonnageOverages: false,
    hasAcceptedRentalOverages: false,
    billingSameAsDelivery: false,
    placementConfirmation: false,
    placementInstructions: '',
    billingCustomerServiceAgreement: false,
    consentToReceiveTexts: false,
    requiredFieldsMet: false,
    preventStartingOver: false,
    selectedCustomerType: '',
    customerTypeHomeowner: {
      canBeSelected: true,
      label: 'Homeowner',
      value: 'personal',
      icon: `${cdn}icons-personal-use.svg`
    },
    customerTypeContractor: {
      canBeSelected: true,
      label: 'Contractor',
      value: 'business',
      icon: `${cdn}icons-for-business.svg`
    },
    isServiceAddressValid: false,
    isBillingAddressValid: false,
    serviceAddress: {
      streetAddress: '',
      streetName: '',
      apartmentNumber: '',
      state: '',
      postalCode: '',
      city: '',
      country: ''
    },
    deliveryDate: '',
    whiteGloveReason: '',
    deliveryDateMilliseconds: '',
    DNIS: '',
    fname: '',
    lname: '',
    company: '',
    email: '',
    phoneNumber: '',
    billing: {
      billingFirstName: '',
      billingLastName: '',
      billingEmailAddress: '',
      billingPhone: '',
      billingAddress: {
        address: '',
        street: '',
        apartmentNumber: '',
        city: '',
        state: '',
        zip: '',
        country: ''
      },
      billingMobilePhoneNumber: '',
      billingCreditCardNumber: '',
      billingExpirationDate: '',
      billingCvv: ''
    }
  }),
  actions: {},
  getters: {
    deliveryDateMilliseconds ({ deliveryDate }) {
      return new Date(deliveryDate).getTime()
    },
    formattedServiceAddress ({ serviceAddress }) {
      let address = (serviceAddress.streetAddress && serviceAddress.streetAddress.length) ? `${serviceAddress.streetAddress} ` : ''
      address += (serviceAddress.streetName && serviceAddress.streetName.length) ? `${serviceAddress.streetName}, ` : ''
      address += (serviceAddress.city && serviceAddress.city.length) ? `${serviceAddress.city}, ` : ''
      address += (serviceAddress.state && serviceAddress.state.length) ? `${serviceAddress.state} ` : ''
      address += (serviceAddress.postalCode && serviceAddress.postalCode.length) ? `${serviceAddress.postalCode}` : ''

      return address
    }
  },
  mutations: {
    gaClientID (state, val) {
      state.gaClientID = val
    },
    isServiceAddressValid (state, val) {
      state.isServiceAddressValid = val
    },
    isBillingAddressValid (state, val) {
      state.isBillingAddressValid = val
    },
    utmSource (state, val) {
      state.utmSource = val
    },
    selectedCustomerType (state, payload) {
      state.selectedCustomerType = payload

      const customerType = payload.charAt(0).toUpperCase() + payload.slice(1) + ' Use'
      dataLayerPush({ event: 'ecom-customer-type', customerType })
    },
    serviceApartmentNumber (state, val) {
      state.serviceAddress.apartmentNumber = val
    },
    deliveryDateMilliseconds (state, val) {
      state.deliveryDateMilliseconds = val
    },
    DNIS (state, val) {
      state.DNIS = val
    },
    preventStartingOver (state, val) {
      state.preventStartingOver = val
    },
    requiredFieldsMet (state, val) {
      state.requiredFieldsMet = val
    },
    billing (state, val) {
      state.billing = val
    },
    serviceAddress (state, val) {
      state.serviceAddress = val
    },
    deliveryDate (state, val) {
      state.deliveryDate = val

      dataLayerPush({ event: 'ecom-delivery-date', deliveryDate: val.toLocaleString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) })
      if (isGoogleEventsReady()) {
        const time = timestamp()

        window.gtag('event', 'Ecommerce - DeliveryDate', {
          event_category: 'Ecommerce',
          event_label: `${time}~${window.location.href}~${val}`,
          send_to: process.env.VUE_APP_UA_ID
        })

        window.gtag('event', 'Ecommerce - DeliveryDate', {
          event_category: 'Ecommerce',
          timestamp: time,
          delivery_date: val,
          bd_client_id: getClientID(),
          send_to: process.env.VUE_APP_GA4_ID
        })
      }
    },
    whiteGloveReason (state, val) {
      state.whiteGloveReason = val
    },
    fname (state, val) {
      state.fname = val
    },
    lname (state, val) {
      state.lname = val
    },
    company (state, val) {
      state.company = val
    },
    email (state, val) {
      state.email = val
    },
    phoneNumber (state, val) {
      state.phoneNumber = val
    },
    placementConfirmation (state, val) {
      state.placementConfirmation = val
    },
    placementInstructions (state, val) {
      state.placementInstructions = val
    },
    billingSameAsDelivery (state, val) {
      state.billingSameAsDelivery = val

      if (val) {
        state.billing.billingFirstName = state.fname
        state.billing.billingLastName = state.lname
        state.billing.billingEmailAddress = state.email
        state.billing.billingPhone = state.phoneNumber
        state.billing.billingAddress.address = `${state.serviceAddress.streetAddress} ${state.serviceAddress.streetName}` || ''
        state.billing.billingAddress.apartmentNumber = state.serviceAddress.apartmentNumber || ''
        state.billing.billingAddress.city = state.serviceAddress.city
        state.billing.billingAddress.state = state.serviceAddress.state
        state.billing.billingAddress.zip = state.serviceAddress.postalCode
        state.billing.billingAddress.country = state.serviceAddress.country
      } else {
        state.billing.billingFirstName = ''
        state.billing.billingLastName = ''
        state.billing.billingEmailAddress = ''
        state.billing.billingPhone = ''
        state.billing.billingAddress.address = ''
        state.billing.billingAddress.apartmentNumber = ''
        state.billing.billingAddress.city = ''
        state.billing.billingAddress.state = ''
        state.billing.billingAddress.zip = ''
        state.billing.billingAddress.country = ''
      }
      if (state.billingSameAsDelivery && state.isServiceAddressValid) {
        state.isBillingAddressValid = true
      }
      if (!state.billingSameAsDelivery) {
        state.isBillingAddressValid = false
      }
    },
    hasAcceptedTonnageOverages (state, val) {
      state.hasAcceptedTonnageOverages = val
    },
    hasAcceptedRentalOverages (state, val) {
      state.hasAcceptedRentalOverages = val
    },
    billingFirstName (state, val) {
      state.billing.billingFirstName = val
    },
    billingLastName (state, val) {
      state.billing.billingLastName = val
    },
    billingEmailAddress (state, val) {
      state.billing.billingEmailAddress = val
    },
    billingPhone (state, val) {
      state.billing.billingPhone = val
    },
    billingAddress (state, val) {
      state.billing.billingAddress.address = val
    },
    billingStreet (state, val) {
      state.billing.billingAddress.street = val
    },
    billingApartmentNumber (state, val) {
      state.billing.billingAddress.apartmentNumber = val
    },
    billingCity (state, val) {
      state.billing.billingAddress.city = val
    },
    billingState (state, val) {
      state.billing.billingAddress.state = val
    },
    billingZip (state, val) {
      state.billing.billingAddress.zip = val
    },
    billingCountry (state, val) {
      state.billing.billingAddress.country = val
    },
    billingCustomerServiceAgreement (state, val) {
      state.billingCustomerServiceAgreement = val
    },
    billingMobilePhoneNumber (state, val) {
      state.billing.billingMobilePhoneNumber = val
    },
    billingCreditCardNumber (state, val) {
      state.billing.billingCreditCardNumber = val
    },
    billingExpirationDate (state, val) {
      state.billing.billingExpirationDate = val
    },
    billingCvv (state, val) {
      state.billing.billingCvv = val
    },
    consentToReceiveTexts (state, val) {
      state.consentToReceiveTexts = val
    }
  }
}
